import React from "react";
import { Helmet } from "react-helmet";
import { ShareSection } from "../../components/template";

const Blog9 = () => {
  const hashtags = [
    "#consciousliving",
    "#ancestralknowledge",
    "#ParamLife",
    "#mindfulliving",
    "#wellnesswisdom",
  ];

  return (
    <>
      {/* 🧠 SEO Meta Tags */}
      <Helmet>
        <title>7 Things Our Grandparents Did Right (But We Ruined)</title>
        <meta
          name="title"
          content="7 Things Our Grandparents Did Right (But We Ruined)"
        />
        <meta
          name="description"
          content="Rediscover 7 timeless habits our grandparents followed for natural health, energy, and balance — and how to bring them back in modern life."
        />
        <meta
          name="keywords"
          content="Indian wellness, Ayurveda habits, timeless wisdom, Param Life, lifestyle balance, mindful living, health after 30, eating with hands, early dinner, seasonal eating, Brahma Muhurta, copper water, Indian traditions"
        />
        <meta name="author" content="Param Life" />
        <meta
          property="og:title"
          content="7 Things Our Grandparents Did Right (But We Ruined)"
        />
        <meta
          property="og:description"
          content="Rediscover 7 timeless habits our grandparents followed for natural health, energy, and balance — and how to bring them back in modern life."
        />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="Param Life" />
        <meta property="og:locale" content="en_IN" />
      </Helmet>

      {/* 🌿 Blog Body */}
      <article className="max-w-4xl mx-auto mt-10 px-6 py-4 leading-relaxed text-gray-800">
        {/* Title */}
        <h1 className="text-3xl font-bold text-secondary mb-8">
          🌿 7 Things Our Grandparents Did Right (But We Ruined)
        </h1>

        {/* Hero Image */}
        <img
          src="/assets/knowyourself/blogs/blog9.jpg"
          alt="7 Things Our Grandparents Did Right (But We Ruined)"
          className="w-[80%] max-w-xl rounded-2xl shadow-md mx-auto mb-10"
        />

        <p className="text-center text-[#578d48] italic mb-8">
          Habits We Replaced — and Paid the Price
        </p>

        {/* 💫 Intro */}
        <section className="space-y-4 mb-8">
          <h2 className="text-2xl font-semibold text-primary">💫 Blog Intro</h2>
          <p>
            Our grandparents didn’t have fitness trackers, diet plans, or
            wellness apps. Yet, they lived long, strong, and peaceful lives —
            with far fewer lifestyle diseases than we see today.
          </p>
          <p>
            They didn’t need “detoxes” or “morning routines.” Their entire life
            was their routine — rooted in nature, simplicity, and awareness.
          </p>
          <p>
            But somewhere between “fast food” and “faster Wi-Fi,” we replaced
            their timeless habits with convenient ones. And today, we’re paying
            the price — with low energy, poor sleep, anxiety, and endless
            scrolling that never feels enough.
          </p>
          <p>
            Here are 7 things our grandparents did right, and how you can bring
            them back — one small change at a time.
          </p>
        </section>

        {/* 1. Eating With Hands */}
        <section className="space-y-4 mb-8">
          <h2 className="text-2xl font-semibold text-primary">
            1. Eating With Hands
          </h2>
          <p>
            Their touch activated digestion before the first bite. Science now
            backs it: the nerve endings in our fingers send signals to the
            stomach that food is coming — awakening digestive enzymes even
            before we chew.
          </p>
          <p>
            But today, we eat with screens, speed, and spoons — mindlessly
            scrolling while feeding both the body and the brain junk.
          </p>

          <p className="font-semibold text-[#b91c1c]">Try This:</p>
          <p>
            Eat one meal a day with your hands. Feel the texture, warmth, and
            connection return. You’ll notice you eat slower, chew better, and
            even feel more satisfied with less food.
          </p>
          <p>Eating with hands isn’t “old-fashioned.” It’s human.</p>
        </section>

        {/* 2. Waking Up With the Sun */}
        <section className="space-y-4 mb-8">
          <h2 className="text-2xl font-semibold text-primary">
            2. Waking Up With the Sun
          </h2>
          <p>
            Our grandparents didn’t need alarm clocks. They rose with the birds
            — tuned to nature’s rhythm. Ayurveda calls this sacred time{" "}
            <span className="text-[#578d48] font-semibold">
              Brahma Muhurta (4–6 AM)
            </span>{" "}
            — when clarity, creativity, and energy are at their peak.
          </p>
          <p>
            Today, we rise with notifications — already anxious before sunrise.
            We check emails before we check in with ourselves.
          </p>

          <p className="font-semibold text-[#b91c1c]">Try This:</p>
          <p>
            Wake up with the first light of dawn, just for three days. Sit
            quietly, stretch, breathe, or go for a walk. You’ll notice a subtle
            shift — a freshness that no coffee can replace.
          </p>
          <p>
            Nature rewards those who move with its rhythm, not against it.
          </p>
        </section>

        {/* 3. Cooking Fresh, Eating Fresh */}
        <section className="space-y-4 mb-8">
          <h2 className="text-2xl font-semibold text-primary">
            3. Cooking Fresh, Eating Fresh
          </h2>
          <p>No fridges. No reheats. No preservatives.</p>
          <p>Just fresh, prana-rich food — cooked with love and presence.</p>
          <p>
            Our grandparents didn’t count calories — they cultivated energy.
            Every meal had a purpose: to nourish, not just fill.
          </p>
          <p>
            Now, we microwave leftovers, order takeout, and wonder why we feel
            dull even after eating.
          </p>

          <p className="font-semibold text-[#b91c1c]">Try This:</p>
          <p>
            Prepare at least one fresh meal a day — no plastic, no processed
            sauces, no reheating. You’ll taste not just better food, but better
            energy.
          </p>
          <p>
            Remember: food loses not just nutrients when stored — it loses life.
          </p>
        </section>

        {/* 4. Using Copper, Clay, and Steel Utensils */}
        <section className="space-y-4 mb-8">
          <h2 className="text-2xl font-semibold text-primary">
            4. Using Copper, Clay, and Steel Utensils
          </h2>
          <p>Each material once had meaning.</p>
          <ul className="list-disc list-inside space-y-1">
            <li>Copper to purify water.</li>
            <li>Clay to cool and balance Pitta.</li>
            <li>Steel to preserve purity and strength.</li>
          </ul>
          <p>
            Today, our kitchens are full of plastic, teflon, and toxins that
            slowly leak into our system. We’ve outsourced wellness to gadgets
            instead of grounding it in our daily tools.
          </p>

          <p className="font-semibold text-[#b91c1c]">Try This:</p>
          <p>
            Start small — drink water from a copper vessel daily. Notice how
            your body begins to respond with lightness and calm.
          </p>
          <p>
            Health isn’t built through big changes. It’s built through conscious
            materials.
          </p>
        </section>

        {/* 5. Sitting on the Floor to Eat */}
        <section className="space-y-4 mb-8">
          <h2 className="text-2xl font-semibold text-primary">
            5. Sitting on the Floor to Eat
          </h2>
          <p>This one’s my favorite.</p>
          <p>
            When you sit cross-legged and eat, your spine straightens, digestion
            improves, and energy stays grounded.
          </p>
          <p>
            Our grandparents didn’t know “posture correction.” They just lived
            it. Now, we eat on couches, hunched over screens, and wonder why our
            backs hurt.
          </p>

          <p className="font-semibold text-[#b91c1c]">Try This:</p>
          <p>
            Try sitting on the floor for one meal a day. Place your plate or
            thali in front of you and eat with full attention.
          </p>
          <p>
            You’ll notice better digestion and even gratitude — because when you
            sit low, you naturally become humble.
          </p>
        </section>

        {/* 6. Eating With the Seasons */}
        <section className="space-y-4 mb-8">
          <h2 className="text-2xl font-semibold text-primary">
            6. Eating With the Seasons
          </h2>
          <p>
            They never forced salads in winter or cold juices during monsoon.
            Their bodies followed nature’s rhythm — light food in summer,
            grounding food in winter, cleansing food in spring.
          </p>
          <p>
            Now, we follow Instagram trends instead of inner intelligence. We
            drink cold smoothies when the body craves warmth and end up with
            weak digestion, bloating, or fatigue.
          </p>

          <p className="font-semibold text-[#b91c1c]">Try This:</p>
          <p>
            Look around your local market. Notice what’s naturally growing right
            now — the fruits, grains, and spices. Nature already knows what your
            body needs this season. Just listen.
          </p>
          <p>Food that grows around you heals you.</p>
        </section>

        {/* 7. Early Dinners, Light Dinners */}
        <section className="space-y-4 mb-8">
          <h2 className="text-2xl font-semibold text-primary">
            7. Early Dinners, Light Dinners
          </h2>
          <p>
            Our grandparents ate by sunset and slept early. Digestion, just like
            the mind, needs rest too.
          </p>
          <p>
            Now, we snack till midnight — often in front of Netflix or while
            scrolling, and call it “unwinding.” But what we’re really doing is
            confusing our body clock.
          </p>

          <p className="font-semibold text-[#b91c1c]">Try This:</p>
          <p>
            Have dinner by 7 PM for three days. You’ll wake up lighter, sleep
            deeper, and feel more energetic through the day.
          </p>
          <p>
            Ayurveda says: eat like a king in the morning, a prince in the
            afternoon, and a pauper at night.
          </p>
        </section>

        {/* 🌙 Closing Thought */}
        <section className="space-y-4 mb-8">
          <h2 className="text-2xl font-semibold text-primary">
            🌙 Closing Thought
          </h2>
          <p>Our grandparents didn’t chase “wellness.” They simply lived well.</p>
          <p>
            Their life wasn’t about doing more — it was about doing what
            mattered, with awareness and rhythm. We may have more comfort today,
            but they had more contentment.
          </p>
          <p>
            It’s time we stop looking for health in apps, supplements, and
            shortcuts… And start bringing back the small, ancient habits that
            truly kept us alive — inside and out.
          </p>
        </section>

        {/* Hashtags */}
        <div className="flex flex-wrap gap-2 mt-10">
          {hashtags.map((tag, idx) => (
            <span
              key={idx}
              className="bg-red-700 bg-opacity-10 text-red-700 hover:bg-red-700 hover:text-white rounded-full px-3 py-1 text-sm transition"
            >
              {tag}
            </span>
          ))}
        </div>

        {/* Share Section */}
        <div className="mt-8">
          <ShareSection />
        </div>
      </article>
    </>
  );
};

export default Blog9;
